import React, { useState, useEffect, useCallback } from "react";
import PageLayout from "../components/PageLayout";
import { theme } from "../theme";
import { ROLE_COLORS, isOwner, isTenantAdmin, getSession, UsersAPI } from "../auth";

// ── Helpers ───────────────────────────────────────────────────────────────────

const Card = ({ children, style = {} }) => (
  <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 12, padding: 20, marginBottom: 20, ...style }}>{children}</div>
);
const SectionTitle = ({ children, color = theme.colors.accent }) => (
  <div style={{ fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700, color, letterSpacing: 1, marginBottom: 14, textTransform: "uppercase" }}>{children}</div>
);
const RoleBadge = ({ role }) => {
  const color = ROLE_COLORS[role] || theme.colors.textMuted;
  return <span style={{ padding: "3px 10px", borderRadius: 4, background: `${color}18`, border: `1px solid ${color}44`, color, fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{role}</span>;
};
const Input = ({ label, value, onChange, type = "text", placeholder = "", disabled = false }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 5 }}>{label}</div>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
      style={{ width: "100%", background: disabled ? theme.colors.inputBgDisabled : theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: disabled ? theme.colors.textMuted : theme.colors.textPrimary, fontFamily: theme.fonts.body, fontSize: 13, padding: "9px 12px", outline: "none", cursor: disabled ? "not-allowed" : "text" }} />
  </div>
);

const PAGE_PERMS = {
  hassaan:      ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel", "Live IDS", "User Management", "Tenant Management"],
  admin:        ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel", "Live IDS", "User Management", "Tenant Management"],
  tenant_admin: ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel", "Live IDS", "User Management"],
  analyst:      ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel", "Live IDS"],
  viewer:       ["Dashboard", "Cyber Alerts", "Reports"],
  guest:        ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel"],
};

const TENANT_ROLES = ["tenant_admin", "analyst", "viewer", "guest"];

function generatePassword(len = 14) {
  const sets = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghjkmnpqrstuvwxyz", "23456789", "!@#$%&*"];
  let pass = sets.map(s => s[Math.floor(Math.random() * s.length)]).join("");
  const all = sets.join("");
  for (let i = pass.length; i < len; i++) pass += all[Math.floor(Math.random() * all.length)];
  return pass.split("").sort(() => Math.random() - 0.5).join("");
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function UserManagement() {
  const session     = getSession();
  const role        = session.role || "";
  const tenantId    = session.tenant_id;
  const tenantName  = session.tenant_name || "Your Company";
  const canManage   = isOwner(role) || isTenantAdmin(role);

  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState("users");
  const [showForm, setShowForm]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast]             = useState("");
  const [emailPreview, setEmailPreview] = useState(null);

  const [form, setForm] = useState({
    username: "", password: generatePassword(),
    full_name: "", email: "",
    role: "analyst", expires_at: "", active: true,
  });

  const f = k => v => setForm(p => ({ ...p, [k]: v }));
  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const loadUsers = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await UsersAPI.list(tenantId);
      setUsers(data.users || []);
    } catch {
      notify("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!form.username.trim()) return alert("Username is required");
    if (form.username.trim().length < 3) return alert("Username must be at least 3 characters");
    if (!form.password || form.password.length < 6) return alert("Password must be at least 6 characters");
    try {
      const created = await UsersAPI.create(tenantId, { ...form, username: form.username.trim().toLowerCase() });
      setEmailPreview({ ...created, plainPassword: form.password });
      setForm({ username: "", password: generatePassword(), full_name: "", email: "", role: "analyst", expires_at: "", active: true });
      setShowForm(false);
      notify("User created!");
      loadUsers();
    } catch (e) {
      alert(e.message || "Failed to create user");
    }
  };

  const handleDelete = async (u) => {
    try {
      await UsersAPI.delete(tenantId, u.id);
      setDeleteConfirm(null);
      notify("User deleted");
      loadUsers();
    } catch (e) {
      alert(e.message || "Failed to delete");
    }
  };

  const handleToggle = async (u) => {
    try {
      await UsersAPI.update(tenantId, u.id, { ...u, active: !u.active });
      notify("Status updated");
      loadUsers();
    } catch {
      notify("Failed to update");
    }
  };

  const handleResetPass = async (u) => {
    const newPass = generatePassword();
    try {
      await UsersAPI.resetPassword(tenantId, u.id, newPass);
      setEmailPreview({ ...u, plainPassword: newPass });
      notify("Password reset — copy it now!");
    } catch {
      notify("Failed to reset password");
    }
  };

  // ── Email template ─────────────────────────────────────────────────────────

  const buildEmail = (u) => {
    const pw = u.plainPassword || "[reset password before sending]";
    const expiry = u.expires_at
      ? `\nAccess Expires: ${new Date(u.expires_at).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}`
      : "\nAccess Expires: No expiry (unlimited)";
    return {
      subject: `Your NexGen Access Credentials — ${tenantName}`,
      body: `Dear ${u.full_name || u.username},

You have been granted access to the NexGen Cyber Intelligence Platform.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  YOUR LOGIN CREDENTIALS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  Portal URL : https://hassaanalrifai.com/nexgen
  Username   : ${u.username}
  Password   : ${pw}
  Access Role: ${u.role.toUpperCase()}
  Company    : ${tenantName}${expiry}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

IMPORTANT SECURITY NOTES:
• These credentials are for your use only — do not share them
• Uploaded datasets are session-only and are NOT stored on our servers
• Your session will expire after 8 hours of inactivity

For questions contact: contact@hassaanalrifai.com

Best regards,
NexGen Cyber Intelligence Platform`
    };
  };

  // ── Access denied ─────────────────────────────────────────────────────────

  if (!canManage) {
    return (
      <PageLayout title="User Management" subtitle="ACCESS CONTROL · ADMIN ONLY">
        <Card style={{ textAlign: "center", padding: "60px 40px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ fontFamily: theme.fonts.display, fontSize: 18, color: theme.colors.danger, letterSpacing: 2 }}>ACCESS DENIED</div>
          <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textMuted, marginTop: 8 }}>
            Requires <strong style={{ color: theme.colors.accent }}>tenant_admin</strong>, <strong style={{ color: theme.colors.accent }}>admin</strong>, or <strong style={{ color: theme.colors.accent }}>hassaan</strong> role.
          </div>
        </Card>
      </PageLayout>
    );
  }

  const tabs = [
    { id: "users",    label: `Users (${users.length})` },
    { id: "roles",    label: "Roles & Permissions" },
    { id: "security", label: "Security" },
  ];

  return (
    <PageLayout title="User Management" subtitle={`ACCESS CONTROL · ${tenantName.toUpperCase()}`}>

      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 24px", background: theme.colors.success, borderRadius: 8, color: theme.colors.buttonText, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 12, letterSpacing: 1, boxShadow: "0 4px 20px rgba(0,230,118,0.4)" }}>
          ✓ {toast}
        </div>
      )}

      {/* Company banner */}
      <Card style={{ borderColor: `${theme.colors.accent}33`, background: `${theme.colors.accent}07`, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontFamily: theme.fonts.display, fontSize: 20, fontWeight: 900, color: theme.colors.accent }}>◫</div>
          <div>
            <div style={{ fontFamily: theme.fonts.display, fontSize: 14, fontWeight: 700, color: theme.colors.textPrimary }}>{tenantName}</div>
            <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>
              Tenant ID: {tenantId} · {users.filter(u => u.active).length} active users · You are managing your company's accounts only
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${theme.colors.cardBorder}`, paddingBottom: 1 }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.id ? `2px solid ${theme.colors.danger}` : "2px solid transparent",
            color: activeTab === tab.id ? theme.colors.danger : theme.colors.textMuted,
            fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer"
          }}>{tab.label.toUpperCase()}</button>
        ))}
      </div>

      {/* ═══ USERS TAB ═══ */}
      {activeTab === "users" && (
        <>
          <Card style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: theme.fonts.display, fontSize: 22, fontWeight: 900 }}>
                <span style={{ color: theme.colors.warning }}>{users.length}</span>
                <span style={{ color: theme.colors.textSecondary, fontSize: 13, marginLeft: 8 }}>USERS</span>
              </div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
                {loading ? "Loading..." : `${users.filter(u => u.active).length} active · ${users.filter(u => u.expires_at && new Date(u.expires_at) < new Date()).length} expired`}
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <button onClick={() => { setShowForm(!showForm); setEmailPreview(null); }} style={{
              padding: "10px 20px",
              background: showForm ? "rgba(255,61,87,0.1)" : `linear-gradient(135deg, ${theme.colors.warning}, #CC9200)`,
              border: showForm ? `1px solid ${theme.colors.danger}` : "none",
              borderRadius: 8, color: showForm ? theme.colors.danger : theme.colors.buttonText,
              fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer"
            }}>{showForm ? "✕ CANCEL" : "+ CREATE USER"}</button>
          </Card>

          {/* Create Form */}
          {showForm && (
            <Card style={{ borderColor: `rgba(255,184,0,0.4)`, background: "rgba(255,184,0,0.04)" }}>
              <SectionTitle color={theme.colors.warning}>NEW USER — {tenantName}</SectionTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Input label="USERNAME" value={form.username} onChange={f("username")} placeholder="john.smith" />
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 5 }}>PASSWORD</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={form.password} onChange={e => f("password")(e.target.value)}
                      style={{ flex: 1, background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.mono, fontSize: 13, padding: "9px 12px", outline: "none" }} />
                    <button onClick={() => f("password")(generatePassword())} style={{ padding: "9px 12px", background: "rgba(0,212,255,0.1)", border: `1px solid rgba(0,212,255,0.3)`, borderRadius: 7, color: theme.colors.accent, fontFamily: theme.fonts.mono, fontSize: 11, cursor: "pointer" }}>↻</button>
                  </div>
                </div>
                <Input label="FULL NAME" value={form.full_name} onChange={f("full_name")} placeholder="John Smith" />
                <Input label="EMAIL" value={form.email} onChange={f("email")} placeholder="john@company.com" />
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 5 }}>ROLE</div>
                  <select value={form.role} onChange={e => f("role")(e.target.value)} style={{ width: "100%", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.body, fontSize: 13, padding: "9px 12px", outline: "none" }}>
                    {TENANT_ROLES.map(r => (
                      <option key={r} value={r}>{r} — {PAGE_PERMS[r]?.join(", ")}</option>
                    ))}
                  </select>
                </div>
                <Input label="ACCESS EXPIRY (optional)" value={form.expires_at} onChange={f("expires_at")} type="date" />
              </div>
              <button onClick={handleCreate} style={{ padding: "11px 28px", background: `linear-gradient(135deg, ${theme.colors.warning}, #CC9200)`, border: "none", borderRadius: 8, color: theme.colors.buttonText, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 12, letterSpacing: 1, cursor: "pointer" }}>
                CREATE USER & GENERATE EMAIL
              </button>
            </Card>
          )}

          {/* Email Preview */}
          {emailPreview && (() => {
            const email = buildEmail(emailPreview);
            return (
              <Card style={{ borderColor: "rgba(0,230,118,0.4)", background: "rgba(0,230,118,0.04)" }}>
                <SectionTitle color={theme.colors.success}>✓ USER CREATED — EMAIL READY</SectionTitle>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, marginBottom: 4 }}>SUBJECT</div>
                  <div style={{ fontFamily: theme.fonts.body, fontSize: 13, color: theme.colors.textPrimary, background: theme.colors.inputBg, borderRadius: 6, padding: "8px 12px" }}>{email.subject}</div>
                </div>
                <pre style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textSecondary, background: theme.colors.inputBg, borderRadius: 8, padding: 16, whiteSpace: "pre-wrap", lineHeight: 1.7, maxHeight: 320, overflowY: "auto", border: `1px solid ${theme.colors.cardBorder}`, marginBottom: 12 }}>
                  {email.body}
                </pre>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button onClick={() => { navigator.clipboard.writeText(email.body); notify("Copied!"); }} style={{ padding: "9px 18px", background: "rgba(0,230,118,0.15)", border: `1px solid rgba(0,230,118,0.4)`, borderRadius: 7, color: theme.colors.success, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>COPY EMAIL</button>
                  <a href={`mailto:${emailPreview.email || ""}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`} style={{ textDecoration: "none" }}>
                    <button style={{ padding: "9px 18px", background: `linear-gradient(135deg, ${theme.colors.success}, #00B85A)`, border: "none", borderRadius: 7, color: theme.colors.buttonText, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>OPEN IN MAIL ↗</button>
                  </a>
                  <button onClick={() => setEmailPreview(null)} style={{ padding: "9px 14px", background: "transparent", border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textMuted, fontFamily: theme.fonts.mono, fontSize: 11, cursor: "pointer" }}>✕</button>
                </div>
              </Card>
            );
          })()}

          {/* Users table */}
          {loading ? (
            <Card style={{ textAlign: "center", padding: 40 }}>
              <div style={{ color: theme.colors.textMuted, fontFamily: theme.fonts.mono, fontSize: 12 }}>Loading users...</div>
            </Card>
          ) : users.length === 0 ? (
            <Card style={{ textAlign: "center", padding: 40 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>◎</div>
              <div style={{ fontFamily: theme.fonts.display, fontSize: 14, color: theme.colors.textSecondary, letterSpacing: 2 }}>NO USERS YET</div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textMuted, marginTop: 6 }}>Create the first user for {tenantName}</div>
            </Card>
          ) : (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: theme.colors.inputBg }}>
                      {["Account", "Role", "Email", "Expires", "Status", "Created By", "Actions"].map(h => (
                        <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, borderBottom: `1px solid ${theme.colors.cardBorder}`, whiteSpace: "nowrap", letterSpacing: 1 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => {
                      const expired = u.expires_at && new Date(u.expires_at) < new Date();
                      return (
                        <tr key={u.id} style={{ borderBottom: `1px solid ${theme.colors.cardBorder}22`, background: expired ? "rgba(255,61,87,0.04)" : i % 2 === 0 ? theme.colors.rowAlt : "transparent" }}>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ fontFamily: theme.fonts.body, fontWeight: 700, fontSize: 13, color: theme.colors.textPrimary }}>{u.username}</div>
                            {u.full_name && <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }}>{u.full_name}</div>}
                          </td>
                          <td style={{ padding: "11px 14px" }}><RoleBadge role={u.role} /></td>
                          <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>{u.email || "—"}</td>
                          <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: expired ? theme.colors.danger : theme.colors.textMuted }}>
                            {u.expires_at ? new Date(u.expires_at).toLocaleDateString() : "No expiry"}
                            {expired && <div style={{ fontSize: 9, color: theme.colors.danger }}>EXPIRED</div>}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            <span style={{ padding: "2px 8px", borderRadius: 4, background: !u.active || expired ? "rgba(255,61,87,0.1)" : "rgba(0,230,118,0.1)", border: `1px solid ${!u.active || expired ? "rgba(255,61,87,0.3)" : "rgba(0,230,118,0.3)"}`, color: !u.active || expired ? theme.colors.danger : theme.colors.success, fontFamily: theme.fonts.mono, fontSize: 9, fontWeight: 700 }}>
                              {expired ? "EXPIRED" : u.active ? "ACTIVE" : "DISABLED"}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>{u.created_by || "—"}</td>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                              <button onClick={() => setEmailPreview(u)} style={{ padding: "4px 10px", background: "rgba(0,230,118,0.1)", border: `1px solid rgba(0,230,118,0.3)`, borderRadius: 5, color: theme.colors.success, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>EMAIL</button>
                              <button onClick={() => handleToggle(u)} style={{ padding: "4px 10px", background: "rgba(255,184,0,0.1)", border: `1px solid rgba(255,184,0,0.3)`, borderRadius: 5, color: theme.colors.warning, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>
                                {u.active ? "DISABLE" : "ENABLE"}
                              </button>
                              <button onClick={() => handleResetPass(u)} style={{ padding: "4px 10px", background: "rgba(0,212,255,0.1)", border: `1px solid rgba(0,212,255,0.3)`, borderRadius: 5, color: theme.colors.accent, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>RESET PW</button>
                              {deleteConfirm === u.id ? (
                                <>
                                  <button onClick={() => handleDelete(u)} style={{ padding: "4px 10px", background: "rgba(255,61,87,0.3)", border: `1px solid ${theme.colors.danger}`, borderRadius: 5, color: theme.colors.danger, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>CONFIRM</button>
                                  <button onClick={() => setDeleteConfirm(null)} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 5, color: theme.colors.textMuted, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer" }}>✕</button>
                                </>
                              ) : (
                                <button onClick={() => setDeleteConfirm(u.id)} style={{ padding: "4px 10px", background: "rgba(255,61,87,0.1)", border: `1px solid rgba(255,61,87,0.3)`, borderRadius: 5, color: theme.colors.danger, fontFamily: theme.fonts.mono, fontSize: 9, cursor: "pointer", fontWeight: 700 }}>DELETE</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ═══ ROLES TAB ═══ */}
      {activeTab === "roles" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
          {Object.entries(PAGE_PERMS).map(([r, pages]) => {
            const color = ROLE_COLORS[r] || theme.colors.textMuted;
            const allPages = ["Dashboard", "Attack Analytics", "Cyber Alerts", "Reports", "Threat Intel", "Live IDS", "User Management", "Tenant Management"];
            return (
              <Card key={r} style={{ borderColor: `${color}33`, background: `${color}07` }}>
                <div style={{ fontFamily: theme.fonts.display, fontSize: 14, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>{r}</div>
                {allPages.map(page => {
                  const has = pages.includes(page);
                  return (
                    <div key={page} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                      <span style={{ color: has ? theme.colors.success : theme.colors.danger, fontSize: 12, flexShrink: 0 }}>{has ? "✓" : "✗"}</span>
                      <span style={{ fontFamily: theme.fonts.body, fontSize: 12, color: has ? theme.colors.textSecondary : theme.colors.textMuted }}>{page}</span>
                    </div>
                  );
                })}
              </Card>
            );
          })}
        </div>
      )}

      {/* ═══ SECURITY TAB ═══ */}
      {activeTab === "security" && (
        <Card>
          <SectionTitle>Security Checklist</SectionTitle>
          {[
            { label: "Tenant isolation enforced — companies cannot see each other's data", ok: true, note: "tenant_id scoped on all API endpoints and WebSocket rooms" },
            { label: "User management scoped to your company only", ok: true, note: "Tenant admins see and manage only their own tenant's users" },
            { label: "Session tokens carry tenant_id", ok: true, note: "HMAC-signed token includes tenant_id — cannot be forged or crossed" },
            { label: "Datasets are session-only (not stored)", ok: true, note: "All uploaded data lives in React memory — never written to server" },
            { label: "Session expiry after 8 hours", ok: true, note: "Users are automatically logged out after 8 hours" },
            { label: "Passwords are bcrypt hashed on the backend", ok: true, note: "Plain passwords never stored or logged" },
            { label: "Seat limits enforced per tenant", ok: true, note: "Cannot exceed seats allocated by platform admin" },
            { label: "HTTPS on production", ok: false, note: "Ensure HTTPS is enabled on your hosting provider" },
            { label: "Rate limiting on login attempts", ok: false, note: "Add server-side rate limiting for production" },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: `1px solid ${theme.colors.cardBorder}22` }}>
              <span style={{ fontSize: 18, color: item.ok ? theme.colors.success : theme.colors.warning, flexShrink: 0 }}>{item.ok ? "✓" : "⚠"}</span>
              <div>
                <div style={{ fontFamily: theme.fonts.body, fontWeight: 600, fontSize: 13, color: item.ok ? theme.colors.textPrimary : theme.colors.warning }}>{item.label}</div>
                <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>{item.note}</div>
              </div>
            </div>
          ))}
        </Card>
      )}

      <div style={{ textAlign: "center", marginTop: 24, fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, paddingBottom: 20 }}>
        NexGen by Hassaan Alrifai · © {new Date().getFullYear()} hassaanalrifai.com
      </div>
    </PageLayout>
  );
}
